import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Text, Cell } from 'recharts';
import { navigationRoutes } from '../constants/constants';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/solid';
import { Dialog, DialogPanel } from '@headlessui/react';
import { useState } from 'react';

const data = [
    {
        name: 'PocketQuant',
        score: 92,
        fill: '#FF7658'
    },
    {
        name: 'GPT-4.0 w/ search',
        score: 31,
        fill: '#76A99D'
    },
    {
        name: 'GPT-4.0 w/o search',
        score: 28,
        fill: '#B0B0B0'
    },
    {
        name: 'Llama 2',
        score: 19,
        fill: '#B0B0B0'
    }
];

const renderCustomBarLabel = ({ x, y, width, value }: { x: number, y: number, width: number, value: string }) => {
    return (
        <Text x={x + width / 2} y={y - 10} fill="#666" textAnchor="middle" dy={-6}>
            {value}
        </Text>
    );
};

export const ComparisonComponent = ({ showNav }: { showNav: boolean }) => {
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const localNavigation = navigationRoutes.filter(e => e.href != '/pq-v-chatgpt')
    localNavigation.push({ name: "Sign Up", href: "/signup" });
    if (window.location.pathname === '/pq-v-chatgpt') {
        document.title = "PocketQuant vs. LLMs | PocketQuant";
    }

    return (
        <div className="benchmark-comparison">
            {showNav && (
                <div className=" inset-x-0 p-6 lg:px-8 top-0 z-50">
                    <div className="flex items-center justify-between" aria-label="Global">
                        <div className="flex lg:flex-1">
                            <a href="/" className="-m-1.5 p-1.5">
                                <span className="sr-only">PocketQuant</span>
                                <img
                                    className="h-20 rounded-lg w-auto"
                                    src="pq-icon-512.png"
                                    alt=""
                                />
                            </a>
                        </div>
                        <div className="hidden lg:flex lg:gap-x-12">
                            {navigationRoutes.map((item) => (
                                <a key={item.name} href={item.href} className="text-sm font-semibold leading-6 text-gray-900">
                                    {item.name}
                                </a>
                            ))}
                        </div>
                        <div className="hidden lg:flex lg:flex-1 lg:justify-end flex items-center">
                            <button onClick={() => {
                                window.location.href = '/signup';
                            }} className="mt-4 py-2 px-4 bg-orange-600 ml-2 text-white font-bold rounded-lg transition duration-300">
                                Sign Up Free
                            </button>

                            <a href="/login" className="mt-4 ml-4 text-sm font-semibold leading-6 text-gray-900">
                                Log in <span aria-hidden="true">&rarr;</span>
                            </a>
                        </div>
                        <div className="flex lg:hidden">
                            <button
                                type="button"
                                className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-gray-700"
                                onClick={() => setMobileMenuOpen(true)}
                            >
                                <span className="sr-only">Open main menu</span>
                                <Bars3Icon className="h-6 w-6" aria-hidden="true" />
                            </button>
                        </div>
                        {
                            window.location.pathname === '/pricing' && (
                                <div className="hidden lg:flex lg:flex-1 lg:justify-end">
                                    <a href="/signup" className="text-sm font-semibold leading-6 text-gray-900">
                                        Sign Up <span aria-hidden="true">&rarr;</span>
                                    </a>
                                </div>
                            )
                        }
                    </div>
                    <Dialog className="lg:hidden" open={mobileMenuOpen} onClose={setMobileMenuOpen}>
                        <div className="fixed inset-0 z-50" />
                        <DialogPanel className="fixed inset-y-0 right-0 z-50 w-full overflow-y-auto bg-white px-6 py-6 sm:max-w-sm sm:ring-1 sm:ring-gray-900/10">
                            <div className="flex items-center justify-between">
                                <a href="/" className="-m-1.5 p-1.5">
                                    <span className="sr-only">PocketQuant</span>
                                    <img
                                        className="h-20 rounded-lg w-auto"
                                        src="pq-icon-512.png"
                                        alt=""
                                    />
                                </a>
                                <button
                                    type="button"
                                    className="-m-2.5 rounded-md p-2.5 text-gray-700"
                                    onClick={() => setMobileMenuOpen(false)}
                                >
                                    <span className="sr-only">Close menu</span>
                                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                                </button>
                            </div>
                            <div className="mt-6 flow-root">
                                <div className="-my-6 divide-y divide-gray-500/10">
                                    <div className="py-6">
                                        <a
                                            href="/signup"
                                            className="-mx-3 block rounded-lg px-3 py-2.5 text-base font-semibold leading-7 text-gray-900 hover:bg-gray-50"
                                        >
                                            Sign Up
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </DialogPanel>
                    </Dialog>
                </div>
            )}
            <h2 className="benchmark-comparison__title" style={{ textAlign: 'center', fontFamily: 'Arial, sans-serif', color: '#333', fontSize: '40px' }}>PocketQuant v. LLMs</h2>
            <h3 className="benchmark-comparison__subtitle" style={{ textAlign: 'center', fontFamily: 'Arial, sans-serif', color: '#333', fontSize: '20px' }}>3x more accurate</h3>
            <div className="benchmark-comparison__chart-shell" style={{ display: 'flex', justifyContent: 'center', padding: '20px', borderRadius: '8px' }}>
                <div className="benchmark-comparison__chart" style={{ width: '60%' }}>
                    <ResponsiveContainer width="100%" height={400} style={{ background: 'transparent' }}>
                        <BarChart
                            data={data}
                            margin={{
                                top: 20, right: 30, left: 20, bottom: 5,
                            }}
                        >
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="name" tick={{ fontSize: 18, fontFamily: 'Arial, sans-serif', fill: '#666' }} />
                            <YAxis
                                type="number"
                                tick={{ fontSize: 18, fontFamily: 'Arial, sans-serif', fill: '#666' }}
                            />
                            <Tooltip />
                            <Bar dataKey="score" barSize={60}>
                                {data.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={entry.fill} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                    <p style={{ textAlign: 'center', fontFamily: 'Arial, sans-serif', fontSize: '18px', color: '#666', marginTop: '10px' }}>
                        Based on <a className='text-blue-500 underline' target="_blank" href="https://github.com/patronus-ai/financebench/tree/main">FinanceBench</a> Top 100 Questions.
                    </p>
                </div>
            </div>
        </div>
    );
}
